import React, { useCallback, useMemo } from 'react'
import { generatePublicUrl } from '../../urlConfig'
import SiteMediaViewer from '../media/SiteMediaViewer'
import FixedButtonsBottomFullScreen from '../button/FixedButtonsBottomFullScreen'
import useTrafficState from '../../hooks/useTrafficState'

const isDirectUrl = (value) => {
    if (!value || typeof value !== 'string') return false

    return value.startsWith('blob:') ||
        value.startsWith('data:') ||
        value.startsWith('http://') ||
        value.startsWith('https://')
}

const uniqueCandidates = (items = []) => {
    const seen = new Set()

    return items.filter((item) => {
        const url = String(item?.url || '').trim()

        if (!url || seen.has(url)) return false

        seen.add(url)
        return true
    })
}

const RenderFullImgModal = ({
    visibleButtonsBottom = false,
    setCurrentIdPicture,
    currentIdPicture,
    setShowModalImg,
    showModalImg,
    items,
    typePage,
    product,
}) => {
    const [trafficState] = useTrafficState()
    const useReducedImages = trafficState !== false

    const safeItems = useMemo(() => {
        const sourceItems = Array.isArray(items) ? items.filter(Boolean) : []

        if (sourceItems.length) {
            return sourceItems
        }

        if (product?.reducedImage) {
            return [{
                _id: `product-thumbnail-${product?._id || 'preview'}`,
                mainImg: true,
                visibleImg: true,
                reducedImage: product.reducedImage,
                fullName: product?.fullName || product?.name || '',
            }]
        }

        return []
    }, [items, product?._id, product?.fullName, product?.name, product?.reducedImage])

    const safeSetShowModalImg = typeof setShowModalImg === 'function'
        ? setShowModalImg
        : () => {}
    const safeSetCurrentIdPicture = typeof setCurrentIdPicture === 'function'
        ? setCurrentIdPicture
        : () => {}

    const activeIndex = useMemo(() => {
        if (!safeItems.length) return 0
        if (!currentIdPicture) return 0

        const index = safeItems.findIndex(
            (item) => String(item?._id || item?.id || '') === String(currentIdPicture)
        )

        return index >= 0 ? index : 0
    }, [currentIdPicture, safeItems])

    const resolveImageUrl = useCallback((raw) => {
        if (!raw) return ''
        if (isDirectUrl(raw)) return raw

        return generatePublicUrl(raw)
    }, [])

    const getOriginalImage = useCallback((obj) => {
        if (obj?.url && isDirectUrl(obj.url)) return obj.url

        const raw = obj?.img
            ? obj.img
            : typePage === 'galleryPage'
                ? obj?.picture?.img
                : null

        return resolveImageUrl(raw)
    }, [resolveImageUrl, typePage])

    const getOptimizedImage = useCallback((obj) => {
        const raw = obj?.reducedImg
            || obj?.optimizedImg
            || obj?.resizedImg
            || obj?.picture?.reducedImg
            || obj?.picture?.optimizedImg
            || ''

        return resolveImageUrl(raw)
    }, [resolveImageUrl])

    const getThumbnailImage = useCallback((obj) => {
        const ownThumbnail = obj?.thumbnail
            || obj?.thumbnailImg
            || obj?.thumb
            || obj?.reducedImage
            || obj?.picture?.thumbnail
            || obj?.picture?.reducedImage
            || ''

        if (ownThumbnail) {
            return resolveImageUrl(ownThumbnail)
        }

        const isMainPicture = obj?.mainImg === true
            || (
                !safeItems.some((item) => item?.mainImg === true) &&
                safeItems[0] === obj
            )

        if (isMainPicture || safeItems.length === 1) {
            return resolveImageUrl(product?.reducedImage || '')
        }

        return ''
    }, [product?.reducedImage, resolveImageUrl, safeItems])

    const getSourceCandidates = useCallback((obj) => {
        const original = getOriginalImage(obj)
        const optimized = getOptimizedImage(obj)
        const thumbnail = getThumbnailImage(obj)

        if (useReducedImages) {
            // В режиме экономии оригинал принципиально не запрашиваем.
            return uniqueCandidates([
                optimized ? { url: optimized, quality: 'optimized' } : null,
                thumbnail ? { url: thumbnail, quality: 'thumbnail' } : null,
            ])
        }

        // В высоком качестве: оригинал -> 854/оптимизированное -> миниатюра.
        return uniqueCandidates([
            original ? { url: original, quality: 'original' } : null,
            optimized ? { url: optimized, quality: 'optimized' } : null,
            thumbnail ? { url: thumbnail, quality: 'thumbnail' } : null,
        ])
    }, [
        getOptimizedImage,
        getOriginalImage,
        getThumbnailImage,
        useReducedImages,
    ])

    const handleIndexChange = useCallback((nextIndex, nextItem) => {
        const nextId = nextItem?._id || nextItem?.id || null
        safeSetCurrentIdPicture(nextId)
    }, [safeSetCurrentIdPicture])

    const handleCloseFullPicture = useCallback(() => {
        safeSetShowModalImg(false)
        safeSetCurrentIdPicture(null)
    }, [safeSetCurrentIdPicture, safeSetShowModalImg])

    const getTitle = useCallback((item) => (
        item?.title ||
        item?.fullName ||
        item?.name ||
        product?.fullName ||
        'Просмотр фотографии'
    ), [product?.fullName])

    const getSubtitle = useCallback((item) => (
        item?.description ||
        item?.subtitle ||
        ''
    ), [])

    if (!safeItems.length) return null

    return (
        <SiteMediaViewer
            open={!!showModalImg}
            items={safeItems}
            activeIndex={activeIndex}
            onActiveIndexChange={handleIndexChange}
            onClose={handleCloseFullPicture}
            getSourceCandidates={getSourceCandidates}
            getItemMimeType={() => 'image/jpeg'}
            getTitle={getTitle}
            getSubtitle={getSubtitle}
            getAlt={getTitle}
            bottomContent={visibleButtonsBottom && product ? (
                <FixedButtonsBottomFullScreen
                    product={product}
                    handleCloseFullPicture={handleCloseFullPicture}
                />
            ) : null}
        />
    )
}

export default RenderFullImgModal
